import React, { useState, useEffect, useRef, useCallback } from 'react';
import loadable from '@loadable/component';

import { Button, Typography, Counter } from '@hpstellar/core';

import isEqual from 'lodash/isEqual';
import { Plus, Cross } from '@hpstellar/icons';

import withPageComponents from '../page/components/with-page-components';
import usePrevious from '../hooks/usePrevious';

import { VWAProductListSortABTest as VWAProductList } from './components/vwa-product-list';
import VWAQuickFilters from './components/vwa-quick-filters';
import VWAHeader from './components/vwa-header';
import SortBar from './components/sort-bar';
import usePage from '../hooks/usePage';
import ETRWCSApi from '../core/src/etr-wcs-api-client';
import FilterModal from './components/filter-modal';
import StickyBar from './components/sticky-bar';
import HeroBanner from './components/hero-banner';
import FilterBar from './components/filter-bar';
import VWAIntopicArticles from './components/vwa-intopic-articles';
import useProductImpressions from '../hooks/useProductImpressionsV2';
import useTestFlags from '../abtest/useTestFlags';

import FilterView from './components/filter-view';
import { Helpers } from '../core/src/helpers';
import {
    getSearchKey,
    sortFacets,
    buildFilterFromUrl,
    buildUrlFromFilters,
    getPageParam,
    DEFAULT_PAGE_SIZE,
    getQueryParams,
    getFacetAddons,
} from './util';

import {
    getClearAllFiltersGtmAttributes,
    generateUniqueProductMapKey,
    removeFromProductMap,
    addGtmUniqueIdToVwaProducts,
} from '../metrics/metrics-helpers';
import { getPartnerLogoContent } from '../utility/setHtml';

import './vwa.less';

const IntersitialContainer = loadable(() => Helpers.retryFunc(() => import('./components/interstitial-container')));
const VWALeftMenu = loadable(() => Helpers.retryFunc(() => import('./components/vwa-left-menu')));
const VWALeftMenuOrion = loadable(() => Helpers.retryFunc(() => import('./components/orion/vwa-left-menu-orion')));
const OrionStyleLoader = loadable(() => Helpers.retryFunc(() => import('./components/orion/orion-style-loader')));

const getRange = values => {
    return values.reduce(
        (acc, item) => {
            const minMax = item.value.split('-').map(x => parseInt(x));
            const [min, max] = minMax;
            acc[0] = acc[0] < min ? acc[0] : min;
            acc[1] = acc[1] > max ? acc[1] : max;
            return acc;
        },
        [99999, 0],
    );
};

const getFilters = (filters, key, urlValue) => {
    const reqFilters = { ...filters };

    // special price case, behaves a bit different than normal facets
    if (key === 'Price' && urlValue?.selection) {
        const range = getRange(urlValue.selection);

        const param = range.join('-');
        if (param === '99999-0') {
            delete reqFilters[key];
        } else {
            reqFilters[key] = { [range.join('-')]: range.join('-') };
        }

        return reqFilters;
    } else if (key === 'Price') {
        delete reqFilters[key];
        return reqFilters;
    }

    if (filters[key] && filters[key][urlValue]) {
        delete reqFilters[key][urlValue];
        if (Object.keys(reqFilters[key]).length < 1) delete reqFilters[key];
    } else if (filters[key]) {
        reqFilters[key][urlValue] = urlValue;
    } else {
        reqFilters[key] = { [urlValue]: urlValue };
    }
    return reqFilters;
};

const buildUrlFromParams = params => {
    if (!params) return '';
    const url = [];
    Object.keys(params)
        .sort()
        .forEach(param => {
            url.push(`${param}=${params[param]}`);
        });
    return url.join('&');
};

const rangeLabel = range => {
    const rangeItems = range.split('-');
    return `$${rangeItems[0]} - $${rangeItems[1]}`;
};

const getAllEnabledFilters = (facets, filters, addonFacets) => {
    const filterCount = Object.keys(filters).length;
    if (filterCount < 1) return [];

    let activeFilters = (facets || []).reduce((a, f) => {
        const subList = f.facetValue
            .filter(v => v.checked === 'true')
            .map(v => {
                if (f.key === 'rating') {
                    let displayName = '';
                    if (v.displayName === 'None') {
                        displayName = 'No Rating';
                    } else {
                        displayName = `${v.displayName[0]} Star`;
                    }
                    return { ...v, displayName: `${displayName}`, key: f.key };
                }

                const displayName = addonFacets?.[f.key]?.valMap?.[v.displayName]?.labelOverride || v.displayName;

                return { ...v, key: f.key, displayName };
            });
        if (subList.length > 0) {
            return [...a, ...subList];
        }
        return a;
    }, []);

    if (filters.Price) {
        const priceRange = Object.values(filters.Price)[0];
        activeFilters = [{ key: 'Price', displayName: rangeLabel(priceRange) }, ...activeFilters];
    }

    return activeFilters;
};

const VWAContent = ({
    slugInfo, //
    innerWidth,
    device,
    history, //
    location, //
    quickFilters: filterContainer,
    finderResults,
    addOnFacets,
    isBot,
    ...rest
}) => {
    const { analyticsData, vanityUrl, rewriteUrl } = slugInfo || {};
    const { page_level } = analyticsData || {};
    const {
        vwaDetails,
        loadMoreGtmAttributes,
        searchKey,
        infiniteScroll,
        targetUrls: interstitialTargetUrls = [],
    } = finderResults || {};

    // TODO: change this to also take into account other params like orderBy and paging
    const [prePath, locationRest] = location.pathname.split('vwa/');
    const [locationSlug, path] = locationRest.split('/');
    //to support rewrite rules use slug from rewrite rule when available
    const [dir, slug = locationSlug] = (rewriteUrl || vanityUrl || '').split('/');
    const params = Helpers.getSearch(location?.search);
    const { orderBy } = params || {};

    const { vwaSort, plpOrion } = useTestFlags(['vwaSort', 'plpOrion']);
    const { behpModel, target = ['laptops', 'desktops', 'printers'] } = vwaSort || {};
    const { enabled: orionEnabled } = plpOrion || {};
    const behpEnabled = behpModel && target.includes(slug) && (!orderBy || orderBy == 6);
    const finderComponentKey = behpEnabled ? 'finder-results-behp' : 'finder-results';
    const additionalParams = behpEnabled
        ? {
              behpDeviceType: device === 'desktop' ? 'desktop' : 'mobile',
              behpModel,
              abtest: undefined,
              _nc: undefined,
          }
        : {};

    const isMobile = innerWidth ? innerWidth < 1024 : device === 'mobile';

    const { products, sortValues, sortGtmActions, totalProducts, beginIndex } = vwaDetails || {};
    const [modalState, setModalState] = useState(isBot);

    const [quickFilterActive, setQuickFilterActive] = useState(
        filterContainer?.quickFilters?.findIndex(f => path?.split(';').some(p => p === f.href)),
    );

    const { updateComponent, updateSeo } = usePage();
    const [filters, setFilters] = useState(buildFilterFromUrl(path));

    const [page, setPage] = useState(parseInt(getPageParam(path)) || 1);
    const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_SIZE * (page || 1) + DEFAULT_PAGE_SIZE);

    //set url using locationSlug. slug cannot be used it accounts for rewrite changes used for ETR API calls
    const [url, setUrl] = useState({
        pathname: prePath + `vwa/${locationSlug}`,
        path,
        params,
        query: location?.search,
    });

    const addOnFacetMap = getFacetAddons(vanityUrl, addOnFacets, filters);

    // A/B test for Orion components
    const LeftMenuComponent = orionEnabled ? VWALeftMenuOrion : VWALeftMenu;

    const prevUrl = usePrevious(url);
    const [presetRange, setPresetRange] = useState([]);
    const enabledFilters = getAllEnabledFilters(vwaDetails?.facets, filters, addOnFacetMap);
    const filterCount = enabledFilters.length;

    // infinite scroll
    const [loadingScroll, setLoadingScroll] = useState(false);
    const scrollObserver = useRef();

    const [loading, setLoading] = useState(false);
    const [loadingList, setLoadingList] = useState();
    const [pageRefs, setPageRefs] = useState({});
    const [pageInView, setPageInView] = useState(page);

    const [interstitialFilterMenuOpen, setInterstitialFilterMenuOpen] = useState(false);

    const isPstore = location?.search.indexOf('pStoreID=') !== -1;
    const [displayInterstitialState, setDisplayInterstitial] = useState(!path);
    const displayInterstitial =
        device === 'desktop' && displayInterstitialState && !isPstore && interstitialTargetUrls.includes(vanityUrl);
    const [settleInterstitial, setSettleInterstitial] = useState(true);

    useEffect(() => {
        if (window && displayInterstitial) {
            history.push('#interstitial');
        }
        setSettleInterstitial(false);
    }, []);

    useEffect(() => {
        if (settleInterstitial) return;
        setDisplayInterstitial(location.hash === '#interstitial');
    }, [path, vanityUrl, location]);

    useEffect(() => {
        if (!infiniteScroll) return () => {};
        products.forEach((p, i) => {
            if (pageRefs[p.page]) return;
            pageRefs[p.page] = React.createRef();
        });
        setPageRefs(pageRefs);
    }, [products]);

    useEffect(() => {
        if (!infiniteScroll) return () => {};
        //forward query params to url, this prevent links with query params from causing an additional history entry
        setUrl({ ...url, path, query: location?.search });
    }, [path, page]);

    useEffect(() => {
        //ignore pageParam path param, prevent reload on page change for infinite scroll
        const urlNoPage = {
            ...url,
            path: (url?.path || '').split(';').filter(p => !p.includes('page=') && p.trim() !== ''),
        };
        const prevUrlNoPage = {
            ...prevUrl,
            path: (prevUrl?.path || '').split(';').filter(p => !p.includes('page=') && p.trim() !== ''),
        };
        if (!isEqual(urlNoPage, prevUrlNoPage) && prevUrl !== undefined) {
            update(buildFilterFromUrl(path));
        }
    }, [url, prevUrl]);

    const update = (updatedFilters, updatedParams) => {
        const updatedPath = buildUrlFromFilters(updatedFilters);
        const { orderBy } = updatedParams || {};
        const queryParams = getQueryParams(slug, updatedFilters, {
            ...url.params,
            ...updatedParams,
            ...additionalParams,
            pageSize: DEFAULT_PAGE_SIZE,
        });
        const { path } = queryParams || {};

        setLoadingList(true);
        ETRWCSApi.component.get('vwa', finderComponentKey, queryParams).then(resp => {
            if (resp) resp.searchKey = getSearchKey(path, orderBy);

            const newOrderFacets = sortFacets(resp?.vwaDetails);
            updateComponent(
                {
                    finderResults: { ...resp },
                },
                state => {
                    const { components } = state;
                    return {
                        components: {
                            ...components,
                            finderResults: {
                                ...resp,
                                vwaDetails: {
                                    ...resp?.vwaDetails,
                                    facets: newOrderFacets,
                                    products: [{ page: 1, results: resp.vwaDetails.products }],
                                },
                                //forward along infinite scroll settings in the finderResults
                                infiniteScroll,
                            },
                        },
                    };
                },
            );

            setFilters(updatedFilters);

            ETRWCSApi.seo.get('vwa', `vwa/${slug}${updatedPath ? `/${updatedPath}` : ''}`, url.params).then(resp => {
                try {
                    //TODO: breadcrumbs wasn't mapping before. Remove this if breadcrumbs should update on filter changes
                    const { breadcrumbs, ...seoData } = resp;
                    updateSeo(seoData, (state, seo) => {
                        const { seo: currentSEO } = state;
                        return { ...state, seo: { ...currentSEO, ...seo } };
                    });
                } catch (e) {}
            });
            setPage(1);
            setLoadingList(false);
        });
    };

    const updateURL = (updatedFilters, updatedParams) => {
        const updatedPath = buildUrlFromFilters(updatedFilters);

        const paramUrl = buildUrlFromParams({ ...url?.params, ...updatedParams });
        const query = paramUrl ? `?${paramUrl}` : '';
        setUrl({ ...url, params: { ...url.params, ...updatedParams }, path: updatedPath, query: query });
        setPageInView(1);
        history.push(`/us-en/shop/vwa/${slug}${updatedPath ? `/${updatedPath}` : ''}${query}`);
    };

    const onSelectFacet = (key, urlValue) => () => {
        // const slug = document.location.pathname.split('vwa/').pop().split('/')[0];

        const reqFilters = getFilters(filters, key, urlValue);
        if (key === 'Price' && !reqFilters?.Price) {
            setPresetRange([]);
            setFilters(reqFilters);
        }

        updateURL(reqFilters);
        setPageIndex(DEFAULT_PAGE_SIZE);
        setQuickFilterActive(-1);
    };

    const onSort = value => {
        removeFromProductMap('finderResults');
        setUrl({ ...url, params: { orderBy: value } });
        updateURL({ ...filters }, { orderBy: value });
        setPageIndex(DEFAULT_PAGE_SIZE);
    };

    const clear = e => {
        e.preventDefault();
        updateURL({});
        setPresetRange([]);
        setQuickFilterActive(-1);
        setPageIndex(DEFAULT_PAGE_SIZE);
    };

    const setQuickFilter = (value, i, restorFilter) => {
        if (i === quickFilterActive) {
            setQuickFilterActive(-1);
            updateURL(restorFilter ? restorFilter : {});
        } else {
            const quickFilter = buildFilterFromUrl(value);
            setQuickFilterActive(i);
            updateURL(quickFilter);
        }
        setPageIndex(DEFAULT_PAGE_SIZE);
        setPresetRange([]);
    };

    const loadMore = (currentPage, backFill = false) => {
        if (infiniteScroll) {
            let updatedPath = getQueryParams(slug, filters, {
                beginIndex: (backFill ? currentPage - 2 : currentPage) * DEFAULT_PAGE_SIZE,
                pageSize: DEFAULT_PAGE_SIZE,
                ...url.params,
                ...additionalParams,
            });
            let { orderBy } = url.params || {};
            let { path } = updatedPath || {};

            setLoading(true);
            ETRWCSApi.component.get('vwa', finderComponentKey, updatedPath).then(resp => {
                setLoadingScroll(false);
                if (resp) resp.searchKey = getSearchKey(path, orderBy);

                updateComponent(
                    {
                        finderResults: resp,
                    },
                    state => {
                        const { components } = state;
                        const nextVwaDetails = resp && resp.vwaDetails;
                        const nextPrds = nextVwaDetails && nextVwaDetails.products;
                        const nextPrdsPage = backFill ? currentPage - 1 : currentPage + 1;
                        const nextResults = addGtmUniqueIdToVwaProducts(nextPrds, { page: nextPrdsPage });
                        const currentPrds = components.finderResults.vwaDetails.products;

                        return {
                            components: {
                                ...components,
                                finderResults: {
                                    ...resp,
                                    vwaDetails: {
                                        ...resp.vwaDetails,
                                        products: backFill
                                            ? [{ page: currentPage - 1, results: nextResults }, ...currentPrds]
                                            : [...currentPrds, { page: currentPage + 1, results: nextResults }],
                                    },
                                    //forward along infinite scroll settings in the finderResults
                                    infiniteScroll,
                                },
                            },
                        };
                    },
                );
                if (!backFill) {
                    setPage(prevState => {
                        return prevState + 1;
                    });
                }
                setPageIndex(pageIndex + DEFAULT_PAGE_SIZE);
                setLoading(false);
            });
        } else {
            let updatedPath = getQueryParams(slug, filters, {
                beginIndex: pageIndex,
                ...url.params,
                ...additionalParams,
            });
            let { orderBy } = url.params || {};
            let { path } = updatedPath || {};

            ETRWCSApi.component.get('vwa', finderComponentKey, updatedPath).then(resp => {
                if (resp) resp.searchKey = getSearchKey(path, orderBy);
                updateComponent(
                    {
                        finderResults: resp,
                    },
                    state => {
                        const { components } = state;
                        const currentProducts = components.finderResults.vwaDetails.products;
                        const newProducts = addGtmUniqueIdToVwaProducts(resp.vwaDetails.products);

                        return {
                            components: {
                                ...components,
                                finderResults: {
                                    ...resp,
                                    vwaDetails: {
                                        ...resp.vwaDetails,
                                        products: [...currentProducts, ...newProducts],
                                    },
                                },
                            },
                        };
                    },
                );
                setPageIndex(pageIndex + DEFAULT_PAGE_SIZE);
            });
        }
    };

    const lastProductRef = useCallback(
        node => {
            // const hasMore = parseInt(totalProducts, 10) - parseInt(page * DEFAULT_PAGE_SIZE, 10) >= 1;
            const hasMore = parseInt(page, 10) * 48 < parseInt(pageIndex, 10);
            if (loadingScroll) return;
            if (scrollObserver.current) scrollObserver.current.disconnect();
            scrollObserver.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && hasMore) {
                    // setPage(page + 1);
                    // history.push(`?${new URLSearchParams({ ...url.params, page: page + 1 }).toString()}`);
                    setLoadingScroll(true);
                    loadMore(page);
                }
            });
            if (node) scrollObserver.current.observe(node);
        },
        [page, loadingScroll],
    );

    const { quickFilters = [], subTitle, entryFilter } = filterContainer || {};

    // for initial page view, the products are included in the page view event
    const productImpressions =
        !displayInterstitial && Array.isArray(products)
            ? products.reduce((allProducts, productData) => {
                  const { results } = productData || {};
                  if (Array.isArray(results)) {
                      allProducts = allProducts.concat(results);
                  }
                  return allProducts;
              }, [])
            : [];

    useProductImpressions({
        componentKey: 'finderResults',
        list: page_level,
        products: productImpressions,
        trackerKey: searchKey,
        impressionGroupSize: 24,
        batchKey: 'vwa',
        disableGtmIdAssignment: true,
    });

    const displayLoadMore = parseInt(totalProducts, 10) - parseInt(beginIndex, 10) > DEFAULT_PAGE_SIZE;

    const mobileFilterButton = (
        <div className="mobileFilterButtonContainer">
            <Button
                className="mobileFilterButton"
                variation="secondary"
                color="dark"
                onClick={() => setModalState(true)}
                size="small"
            >
                Sort & Filter
                {filterCount > 0 && ` (${filterCount})`}
            </Button>
        </div>
    );

    const loadTop = e => {
        e.preventDefault();
        const hasTop = vwaDetails?.products[0].page === 1;

        if (hasTop) {
            const elem = pageRefs[1]?.current;
            elem.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        } else {
            update(buildFilterFromUrl(path));
        }
    };

    const loadPrev = useCallback(
        e => {
            e.preventDefault();
            if (page === 1) return;
            const hasPrev = vwaDetails?.products[0].page != pageInView;

            if (hasPrev) {
                const elem = pageRefs[pageInView - 1]?.current;
                elem.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
            } else {
                loadMore(pageInView, true);
            }
        },
        [page, pageInView, pageRefs],
    );

    const sortBar = (
        <SortBar
            filterCount={filterCount}
            clear={clear}
            vwaDetails={vwaDetails}
            sortValues={sortValues}
            sortGtmActions={sortGtmActions}
            onSort={onSort}
            isInfiniteScroll={infiniteScroll}
            loadTop={loadTop}
            loadPrev={loadPrev}
            pageInView={pageInView}
            history={history}
            url={url}
            width={innerWidth}
            openFilters={() => setModalState(true)}
        />
    );

    const quickFilterTitle = (
        <Typography variant="bodyS" className="quickFilterTitle" isCaps>
            {`${subTitle}${!isMobile ? ' :' : ''}`}
        </Typography>
    );

    const filterClear = filterCount > 0 && (
        <>
            <Counter className="counter" count={filterCount} size="small" subsetDelimiter="of">
                FILTER{filterCount > 1 ? 'S' : ''} APPLIED
            </Counter>
            <Button
                className="clear"
                variation="tertiary"
                onClick={clear}
                endIcon={<Cross size="xs" />}
                to={prePath + `vwa/${locationSlug}`}
                {...getClearAllFiltersGtmAttributes()}
            >
                CLEAR ALL
            </Button>
        </>
    );

    const filterBar =
        filterCount > 0 ? (
            <FilterBar
                filterClear={filterClear}
                onSelectFacet={onSelectFacet}
                filters={enabledFilters}
                isMobile={isMobile}
            />
        ) : null;

    const filterView =
        filterCount > 0 ? (
            <FilterView
                filterClear={filterClear}
                onSelectFacet={onSelectFacet}
                filters={enabledFilters}
                isMobile={isMobile}
                orionEnabled={orionEnabled}
            />
        ) : null;

    if (!vwaDetails) return null;

    if (displayInterstitial) {
        return (
            <>
                <IntersitialContainer
                    quickFilters={quickFilters}
                    width={innerWidth}
                    setQuickFilter={setQuickFilter}
                    interstitialFilterMenuOpen={interstitialFilterMenuOpen}
                    setInterstitialFilterMenuOpen={setInterstitialFilterMenuOpen}
                    setDisplayInterstitial={setDisplayInterstitial}
                    addOnFacets={addOnFacets}
                    vanityUrl={vanityUrl}
                    templateKey={slugInfo?.templateKey}
                >
                    <LeftMenuComponent
                        facets={vwaDetails.facets}
                        expandedFacets={vwaDetails.expandedFacets}
                        onSelectFacet={onSelectFacet}
                        vanityUrl={slugInfo?.vanityUrl}
                        filters={filters}
                        presetRange={presetRange}
                        setPresetRange={setPresetRange}
                        addOnFacets={addOnFacets}
                        interstitial
                        onCloseInterstitial={() => setInterstitialFilterMenuOpen(false)}
                        fullWidth
                        isMobile={false}
                        quickFilters={quickFilters}
                        onClickQuickFilter={(f, i) => setQuickFilter(f.href, i)}
                    />
                </IntersitialContainer>
                <VWAIntopicArticles />
            </>
        );
    }

    return (
        <>
            {orionEnabled && <OrionStyleLoader />}
            {quickFilters?.length > 0 && (
                <div className="quickFilters">
                    {isMobile && quickFilterTitle}
                    <VWAQuickFilters
                        quickFilters={quickFilters}
                        quickFilterActive={quickFilterActive}
                        setQuickFilter={setQuickFilter}
                        quickFilterTitle={quickFilterTitle}
                        isMobile={isMobile}
                        entryFilter={entryFilter}
                    />
                </div>
            )}
            <HeroBanner loading="eager" prefetch={true} prefetchMinWidth={0} />
            {infiniteScroll && (
                <>
                    <StickyBar id="vwaStickyBar" isInfiniteScroll>
                        <div className="pageAndSort">
                            {innerWidth > 767 && innerWidth < 1024 && mobileFilterButton}
                            {sortBar}
                        </div>
                    </StickyBar>
                    {innerWidth > 767 && filterBar}
                </>
            )}

            {!infiniteScroll && (
                <>
                    <StickyBar id="vwaStickyBar" height={isMobile ? '84px' : '60px'}>
                        {isMobile ? mobileFilterButton : sortBar}
                    </StickyBar>
                    {isMobile && sortBar}
                </>
            )}

            <div className={`content${orionEnabled ? ' orion' : ''}`}>
                {!isMobile && (
                    <LeftMenuComponent
                        facets={vwaDetails.facets}
                        expandedFacets={vwaDetails.expandedFacets}
                        onSelectFacet={onSelectFacet}
                        vanityUrl={slugInfo?.vanityUrl}
                        filters={filters}
                        presetRange={presetRange}
                        setPresetRange={setPresetRange}
                        addOnFacets={addOnFacets}
                        notice
                    />
                )}
                <VWAProductList
                    products={products}
                    slugInfo={slugInfo}
                    history={history}
                    loading={loading}
                    pageRefs={pageRefs}
                    page={page}
                    pageInView={pageInView}
                    setPageInView={setPageInView}
                    infiniteScroll={infiniteScroll}
                    filterCount={filterCount}
                    url={url}
                    loadingList={loadingList}
                    maxResultsPerPage={DEFAULT_PAGE_SIZE}
                    orionEnabled={orionEnabled}
                >
                    {displayLoadMore && !loading && (
                        <div className="loadMore">
                            <Button
                                variation="secondary"
                                endIcon={<Plus size="s" />}
                                onClick={() => loadMore(page)}
                                {...(loadMoreGtmAttributes || {})}
                            >
                                Load more
                            </Button>
                        </div>
                    )}
                </VWAProductList>
            </div>
            {isMobile && (
                <FilterModal
                    isOpen={modalState}
                    filterCount={filterCount}
                    resultsCount={vwaDetails.totalProducts}
                    onModalClose={() => setModalState(false)}
                    clearFilters={clear}
                    hidden={isBot}
                    vwaVariant
                    orionEnabled={orionEnabled}
                >
                    {filterView}

                    <LeftMenuComponent
                        facets={vwaDetails.facets}
                        expandedFacets={vwaDetails.expandedFacets}
                        onSelectFacet={onSelectFacet}
                        aligned="left"
                        sortValues={sortValues}
                        sortValue={vwaDetails.orderBy}
                        onSort={onSort}
                        vanityUrl={slugInfo?.vanityUrl}
                        filters={filters}
                        presetRange={presetRange}
                        setPresetRange={setPresetRange}
                        addOnFacets={addOnFacets}
                        filterView={filterView}
                        visibleCount={vwaDetails.visibleCount}
                        truncationSettings={{
                            truncationExpandText: 'View More',
                            truncationCollapseText: 'View Less',
                        }}
                    />
                </FilterModal>
            )}
        </>
    );
};

export default withPageComponents(VWAContent, {
    components: ['quickFilters', 'finderResults', 'addOnFacets'],
    device: true,
});
